import loadable from '@loadable/component'
import * as React from 'react'
import { match, Switch } from 'react-router'

import TripGuard from './components/TripGuard/TripGuard'
import RootWrapper from './pages/RootWrapper'
import Trip404Page from './pages/Trip404Page'
import { isExploreEnabled } from './selectors'

import AuthRoute from 'components/App/Routes/AuthRoute'
import Route from 'components/App/Routes/TrackedRoute'
import config from 'constants/config'
import { useAppSelector } from 'hooks/reduxHooks'
import WebViewRoutes from 'tripPlanner/pages/WebViews/WebViewRoutes'

const MyTripsPage = loadable(
  () =>
    import(
      /* webpackChunkName: "MyTripsPage" */ 'tripPlanner/pages/myTrips/MyTripsPage'
    ),
)

const CuratedTripsPage = loadable(
  () =>
    import(
      /* webpackChunkName: "CuratedTripsPage" */ 'tripPlanner/pages/curatedTrips/CuratedTripsPage'
    ),
)

const PublicTripParent = loadable(
  () =>
    import(
      /* webpackChunkName: "TripPlannerPublicTripParent" */ 'tripPlanner/pages/publicTrip/PublicTripParent'
    ),
)

const TripPlannerAboutPage = loadable(
  () =>
    import(
      /* webpackChunkName: "TripPlannerAboutPage" */ 'tripPlanner/pages/about/TripPlannerAboutPage'
    ),
)

const TripParent = loadable(
  () =>
    import(
      /* webpackChunkName: "TripPlannerTripParent" */ 'tripPlanner/pages/trip/TripParent'
    ),
)

const CuratedTripParent = loadable(
  () =>
    import(
      /* webpackChunkName: "TripPlannerCuratedTripParent" */ 'tripPlanner/pages/curatedTrip/CuratedTripParent'
    ),
)

const EditTripParent = loadable(
  () =>
    import(
      /* webpackChunkName: "TripPlannerEditTripParent" */ 'tripPlanner/pages/editTrip/EditTripParent'
    ),
)

const AcceptInvite = loadable(
  () =>
    import(
      /* webpackChunkName: "TripPlannerAcceptInvite" */ './pages/trip/AcceptInvite'
    ),
)

const CreateFromOrder = loadable(
  () =>
    import(
      /* webpackChunkName: "TripPlannerCreateFromOrder" */
      './pages/trip/CreateFromOrder'
    ),
)

const HiddenSettings = loadable(
  () =>
    import(
      /* webpackChunkName: "HiddenSettings" */
      './pages/hiddenSettings/HiddenSettings'
    ),
)

const pageRoute = (match: { path: string }, path: string) =>
  `${match.path}${path}`

interface Props {
  match: match
}

function TripPlannerRoutes({ match }: Props) {
  const exploreEnabled = useAppSelector(isExploreEnabled)

  return (
    <TripGuard fallback={<Trip404Page />}>
      <RootWrapper>
        <Switch>
          <Route
            exact
            path={pageRoute(match, '/trip-planner/about')}
            component={TripPlannerAboutPage}
          />
          <Route
            exact
            path={pageRoute(match, '/trip-planner/curated')}
            component={CuratedTripsPage}
          />
          <AuthRoute
            showPopup
            exact
            path={pageRoute(match, '/trip-planner')}
            component={MyTripsPage}
          />
          <AuthRoute
            showPopup
            exact
            path={pageRoute(
              match,
              exploreEnabled
                ? '/trip-planner/trip/:tripId/:tab(summary|itinerary|explore)'
                : '/trip-planner/trip/:tripId/:tab(summary|itinerary)',
            )}
            component={TripParent}
          />
          {config.TRIP_PLANNER_ENABLE_EDIT_MODE && (
            <AuthRoute
              showPopup
              exact
              path={pageRoute(match, '/trip-planner/trip/:tripId/edit')}
              component={EditTripParent}
            />
          )}
          {config.TRIP_PLANNER_ENABLE_PUBLIC_TRIPS && (
            <Route
              exact
              path={pageRoute(
                match,
                '/trip-planner/public-trip/:tripId/:tab(summary|itinerary)',
              )}
              component={PublicTripParent}
            />
          )}
          <AuthRoute
            showPopup
            exact
            path={pageRoute(
              match,
              '/trip-planner/trip/:tripId/accept-invite/:inviteId',
            )}
            component={AcceptInvite}
          />
          <Route
            exact
            path={pageRoute(match, '/trip-planner/curated/:tripId/summary')}
            component={CuratedTripParent}
          />
          <AuthRoute
            showPopup
            exact
            path={pageRoute(match, '/trip-planner/create')}
            component={CreateFromOrder}
          />
          <AuthRoute
            showPopup
            exact
            path={pageRoute(match, '/trip-planner/hidden-settings')}
            component={HiddenSettings}
          />
          <Route path={pageRoute(match, '/trip-planner/webView')}>
            <WebViewRoutes match={match} />
          </Route>
          <Trip404Page />
        </Switch>
      </RootWrapper>
    </TripGuard>
  )
}

export default TripPlannerRoutes
